/**
 * Type and layout metrics lifted off the official Form-13 PDF.
 *
 * Every number was measured on a 4× render of the government file (page
 * 595.2 × 841.7 pt, so 1 pt = 1 px here): row baselines and label/leader x
 * positions come from the ink extents, the dot pitch from the printed leaders.
 * The preview places its rows at exactly these coordinates, so a printed page
 * lines up with the real form.
 */

/** Page box — A4 at 72 dpi, same as the original. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/** Font sizes (px = pt) and weights, matched to the printed line widths. */
export const TYPE = {
    title: { size: 16, weight: 740 },
    rule: { size: 11, weight: 400 },
    mainTitle: { size: 12, weight: 400 },
    body: { size: 11.3, weight: 400 },
    note: { size: 10.5, weight: 400 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/**
 * Kalpurush's ascent is 1 em and its descent 0.4 em, so in that line box the
 * baseline lands 1.05 em below the top — the number that turns a measured
 * baseline into a CSS `top`.
 */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/**
 * The leaders are real full-stops in the form's own font — 1.67 pt of ink every
 * 3.25 pt — not a CSS border. Kalpurush's period advances 0.25 em, so the run
 * needs a little tracking on top.
 */
export const LEADER = {
    char: '.',
    /** Enough dots to overflow the longest line; each run is clipped. */
    count: 240,
    letterSpacing: 3.25 - TYPE.body.size * 0.25,
};

/**
 * The typed answers are not part of the printed form, so they do not have to
 * match its face: they print larger and bolder than the labels to stay legible
 * on a photocopy. A value that would overrun its blank shrinks down toward
 * `minSize` instead of being clipped — a full migration reason at 9.6 pt beats
 * half of one at body size.
 */
export const VALUE_TYPE = { size: 13.2, minSize: 9.6, weight: 400 };

/**
 * Typed values sit above the dots, the way handwriting does — far enough that a
 * Latin descender (g, j, p, q, y) clears the leader instead of sitting on it.
 */
export const VALUE_LIFT = 8;

/** …and start a few points in from the label, not hard against it. */
export const VALUE_INSET = 6;

/** Page 1 — every filled-in line: label, its leader, and the field it prints. */
export const PAGE1_FIELDS = [
    { y: 173.1, x: 97.8, label: 'officeUpazilaLabel', from: 153.1, to: 215, value: 'officeUpazila' },
    { y: 194.6, x: 97.4, label: 'officeDistrictLabel', from: 119.0, to: 213, value: 'officeDistrict' },

    { y: 246.8, x: 97.5, num: '১', label: 'applicantLabel', from: 182.1, to: 576, value: 'applicantName' },
    { y: 270.8, x: 97.5, num: '২', label: 'nidLabel', from: 236.1, to: 575, value: 'nid' },
    { y: 300.6, x: 97.5, num: '৩', label: 'birthDateLabel', from: 151.1, to: 574, value: 'birthDate' },

    { y: 345.9, x: 136.5, label: 'voterNoLabel', from: 195.1, to: 442, value: 'currentVoterNo' },
    { y: 367.9, x: 136.5, label: 'areaNameLabel', from: 224.1, to: 361, value: 'currentAreaName' },
    { y: 367.9, anchorRight: true, label: 'areaNoLabel', from: 455.2, to: 575, value: 'currentAreaNo' },
    { y: 391.4, x: 136.5, label: 'upazilaLabel', from: 202.1, to: 363, value: 'currentUpazila' },
    { y: 391.4, anchorRight: true, label: 'districtLabel', from: 396.6, to: 576, value: 'currentDistrict' },
    { y: 413.9, x: 136.5, label: 'villageLabel', from: 237.6, to: 385, value: 'currentVillage' },
    { y: 413.9, anchorRight: true, label: 'holdingLabel', from: 475.2, to: 572, value: 'currentHolding' },

    { y: 458.7, x: 136.5, label: 'districtLabel', from: 168.6, to: 344, value: 'newDistrict' },
    { y: 458.7, anchorRight: true, label: 'upazilaLabel', from: 412.6, to: 576, value: 'newUpazila' },
    { y: 481.4, x: 136.5, label: 'localBodyLabel', from: 335.6, to: 483, value: 'newLocalBody' },
    { y: 481.4, anchorRight: true, label: 'wardNoLabel', from: 535.2, to: 575, value: 'newWardNo' },
    { y: 502.7, x: 136.5, label: 'areaNameLabel', from: 227.6, to: 373, value: 'newAreaName' },
    { y: 502.7, anchorRight: true, label: 'areaNoLabel', from: 468.7, to: 565, value: 'newAreaNo' },
    { y: 526.2, x: 136.5, label: 'villageLabel', from: 237.6, to: 385, value: 'newVillage' },
    { y: 526.2, anchorRight: true, label: 'holdingLabel', from: 475.2, to: 572, value: 'newHolding' },
    { y: 548.9, x: 136.5, label: 'phoneLabel', from: 266.1, to: 504, value: 'phone' },
    { y: 571.1, x: 136.5, label: 'postOfficeLabel', from: 176.1, to: 356, value: 'postOffice' },

    { y: 623.0, x: 97.5, num: '৬', label: 'stayingSinceLabel', from: 371, to: 577, value: 'stayingSince' },
    { y: 645.2, x: 97.5, num: '৭', label: 'reasonLabel', from: 169.6, to: 574, value: 'migrationReason' },
];

/** Page 1 headings and fixed text. */
export const PAGE1_TEXT = {
    title: { y: 59.0, center: 318 },
    rule: { y: 76.3, center: 318 },
    mainTitle: { y: 105.8, center: 319 },
    to: { y: 134.3, x: 60 },
    officer: { y: 150.6, x: 97.8 },
    nidNote: { y: 285.6, right: 492 },
    currentTitle: { y: 324.9, x: 97.5, num: '৪', numX: 60 },
    newTitle: { y: 436.9, x: 99, num: '৫', numX: 61 },
};

/** The item numbers print in their own column at x = 60. */
export const NUMBER_X = 60;

/** ডাকঘর row: the post-code cells to the right of it. */
export const POST_CODE = {
    y: 571.1,
    labelRight: 470,
    boxLeft: 483.4,
    boxWidth: 25,
    boxTop: 559.1,
    boxHeight: 23.5,
};

/** Page 2 — the সনাক্তকারী block is the only part the applicant fills in. */
export const PAGE2_FIELDS = [
    { y: 344.9, x: 187, label: 'identifierSignatureLabel', from: 339.6, to: 553, value: null },
    { y: 366.6, anchorRight: true, label: 'identifierNameLabel', from: 338.1, to: 551, value: 'identifierName' },
    { y: 388.4, anchorRight: true, label: 'identifierNidLabel', from: 406.6, to: 555, value: 'identifierNid' },
    { y: 410.4, anchorRight: true, label: 'identifierAddressLabel', from: 342.6, to: 556, value: 'identifierAddress' },
];

/** Page 2 fixed text, leaders and captions. */
export const PAGE2_TEXT = {
    docsHead: { y: 133.5, x: 71, num: '৮', numX: 33 },
    docs: [
        { y: 155.8, x: 71, wrapX: 90, wrapY: 179.1 },
        { y: 200.1, x: 71 },
        { y: 221.6, x: 71 },
    ],
    applicantSignature: { leaderY: 271.3, from: 424, to: 559, y: 285.6, center: 495 },
    officeOnly: { y: 462.7, center: 295 },
    decision: {
        line1: { y: 495.7, x: 71, from: 203, to: 320, tailX: 325 },
        // Kalpurush sets 'নাম কর্তন এবং' wider than the original face, so its
        // leader starts where the words actually end.
        line2: { y: 515.4, x: 32, from: 118, to: 205, tailX: 209 },
    },
    officerSignature: { leaderY: 572, from: 434, to: 559, y: 584.2, center: 496 },
    officerDate: { leaderY: 605, from: 437, to: 521 },
    separator: { y: 664, from: 32, to: 570 },
    receiptTitle: { y: 680.7, center: 295 },
    receiptName: { y: 714.2, x: 32, from: 104, to: 393, tailX: 397 },
    receiptFormNo: { y: 733.7, x: 27, from: 111.5, to: 372 },
    receiverSignature: { leaderY: 772, from: 458, to: 556, y: 790.3, center: 510 },
};
